import React, { useState, useEffect, useRef, ChangeEvent, useImperativeHandle } from 'react';
import {
    Box,
    TextField,
    Typography,
    CircularProgress,
    Alert,
    Stack,
    InputAdornment,
    IconButton,
    Button,
    Divider
} from '@mui/material';

import { useStartSmsAuthMutation, useVerifySmsCodeMutation } from '../../features/oidcSlice';
import { USCountryType } from '../../app/components/CountryCodes';
import { printError } from '../../app/utils';
import ClearIcon from "@mui/icons-material/Clear";

import { GetCountryTypeByLabelDefualtToUs } from '../../app/components/CountryCodes';
import { useAuthProfile } from '../../app/hooks';
import { CUSTOM_METHOD } from '../../app/auth/utils';
import { smsDisclosure, smsDisclosureOption, smsSupportContact, smsTermsOfUseAndPrivacy } from '../iam/smsDisclosure';


/** Occupies exactly the checkbox column so text with no checkbox of its own
 *  lines up with the checkbox labels' text. A real hidden input rather than a
 *  padding value: the native checkbox's width AND its UA margins (Chrome adds
 *  margin-left: 4px, which the inline marginRight does not override) are
 *  browser metrics, so any hard-coded indent is wrong by a few px. */
const CheckboxGutter: React.FC = () => (
    <input
        type="checkbox"
        disabled
        tabIndex={-1}
        aria-hidden="true"
        style={{ marginRight: '8px', visibility: 'hidden' }}
    />
);

export interface SmsConsentProps {
    consentChecked: boolean | undefined;
    onConsentChange: (checked: boolean) => void;
    smsConsentRef?: React.Ref<{ getCurrentConsent(): boolean | undefined }>;
    /** Render Terms of Use / Privacy Policy as its own REQUIRED checkbox instead of
     *  inline "by continuing" text. Login only: the profile wizard's Communicate step
     *  runs after the user has already accepted terms, so it keeps the inline form. */
    showTermsAcceptance?: boolean;
    termsAccepted?: boolean;
    onTermsAcceptedChange?: (checked: boolean) => void;
}

export const SmsConsent: React.FC<SmsConsentProps> = ({
    consentChecked,
    onConsentChange,
    smsConsentRef,
    showTermsAcceptance = false,
    termsAccepted = false,
    onTermsAcceptedChange,
}) => {
    const consentCheckboxRef = useRef<HTMLInputElement>(null);
    const nonConsentCheckboxRef = useRef<HTMLInputElement>(null);
    const termsOfUseAndPrivacyCheckboxRef = useRef<HTMLInputElement>(null);

    // Expose method to get current consent state
    useImperativeHandle(smsConsentRef, () => ({
        getCurrentConsent: (): boolean | undefined => {
            if (consentCheckboxRef.current?.checked) return true;
            if (nonConsentCheckboxRef.current?.checked) return false;
            return undefined;
        }
    }), []);

    const handleConsentChange = (
        event: ChangeEvent<HTMLInputElement>
    ): void => {
        const isChecked = event.target.checked;
        
        // Immediate DOM-based mutual exclusivity
        if (isChecked && nonConsentCheckboxRef.current) {
            nonConsentCheckboxRef.current.checked = false;
        }
        
        // Trigger parent change with correct state
        onConsentChange(isChecked);
    };

    const handleNonConsentChange = (
        event: ChangeEvent<HTMLInputElement>
    ): void => {
        const isChecked = event.target.checked;
        
        // Immediate DOM-based mutual exclusivity
        if (isChecked && consentCheckboxRef.current) {
            consentCheckboxRef.current.checked = false;
        }
        
        // Trigger parent change with correct state (negated for non-consent)
        onConsentChange(!isChecked);
    };

    return (
        <>
            <Box sx={{
                borderRadius: 2,
                backgroundColor: 'rgba(101, 110, 255, 0.04)',
                border: '1px solid rgba(164, 142, 233, 0.12)',
                p: 1.5,
                mb: 1.5,
            }}>
                <Stack spacing={1.5}>
                    <Typography component="label" sx={{ display: "flex", alignItems: "flex-start", cursor: "pointer" }}>
                        <input
                            ref={consentCheckboxRef}
                            type="checkbox"
                            checked={consentChecked === true}
                            onChange={handleConsentChange}
                            style={{ marginRight: '8px', marginTop: '2px' }}
                        />

                        <Box>
                            {smsDisclosure()}
                        </Box>
                    </Typography>

                    {showTermsAcceptance ? (
                        <>
                            <Box sx={{ display: 'flex' }}>
                                <CheckboxGutter />
                                <Box>{smsSupportContact()}</Box>
                            </Box>
                            <Divider />
                            {/* Separate REQUIRED acceptance. Kept out of the messaging
                                consent above so an SMS opt-in is never bundled with a
                                terms acceptance — carriers treat consent as single-purpose. */}
                            {/* alignItems center, unlike the multi-line consent row above:
                                this label is a single line, so top-aligning it reads as
                                sitting on the checkbox's bottom edge.
                                variant/lineHeight must MATCH the caption content — a default
                                body1 strut (16px/1.5) around 12px glyphs puts the baseline low
                                in the line box, so the text reads as sinking below the
                                checkbox even with center alignment (and renders the `*` oversized). */}
                            <Typography component="label" variant="caption" sx={{ display: "flex", alignItems: "center", cursor: "pointer", lineHeight: 1.5 }}>
                                <input
                                    ref={termsOfUseAndPrivacyCheckboxRef}
                                    type="checkbox"
                                    checked={termsAccepted}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => onTermsAcceptedChange?.(event.target.checked)}
                                    style={{ marginRight: '8px' }}
                                />
                                <Box sx={{ lineHeight: 1.5 }}>
                                    <Box component="span" sx={{ color: 'error.main', mr: 0.5 }}>*</Box>
                                    {smsTermsOfUseAndPrivacy("I agree to ", true)}
                                </Box>
                            </Typography>
                        </>
                    ) : (
                        <Box sx={{ display: 'flex' }}>
                            <CheckboxGutter />
                            <Box>
                                {smsTermsOfUseAndPrivacy("By continuing, you agree to ")}
                                {smsSupportContact()}
                            </Box>
                        </Box>
                    )}
                </Stack>
            </Box>
        </>
    );
};
